// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The 3-D view, as the harness uses it.
//
// A small amount of glue that nobody else should have to write twice: create the host, hand it
// a `MachineScene`, drive the camera from a preset, and report what happened. The site shell
// (M3-37) will mount the same two objects; the only thing that belongs here rather than there
// is the preset cycling, which is a harness affordance.
//
// **Created lazily and allowed to fail.** WebGL is not guaranteed — a locked-down browser, a
// blocklisted driver, a machine with no GPU at all — and the 2-D collision view is the harness's
// reason for existing. A failure to get a context must cost the diagnostic instrument nothing.

import { Vector3 } from 'three'
import type { LoadedMachine } from '@core/machine'
import { SceneHost } from './SceneHost'
import type { DmdRaster } from '../fx/dmd'
import type { PresentationEvents } from '../engine/eventFeed'
import type { LampStateReader } from './lamps'
import { MachineScene } from './MachineScene'
import type { ComponentLightingState } from './componentLightingState'
import { CameraShake, createShakeOffset, type ShakeOffset } from './cameraShake'
import { MOTION_STEPS, MotionPreference } from './motionPreference'
import type { ToyStateReader } from './toys'
import {
  CAMERA_PRESETS,
  cameraPose,
  fitEye,
  fovForAspect,
  orbitFrom,
  orbitPosition,
  type CameraPreset,
  type MachineFrame,
} from './cameras'
import { attachPointerOrbit, OrbitDrag } from '../input/pointerOrbit'
import { tierBelow, type RenderTier } from './tiers'
import { fitsFrameBudget } from './capability'
import { TierChoice } from './tierChoice'
import { FrameGovernor } from './frameGovernor'

/**
 * Everything optional the view can be given.
 *
 * An options object rather than a run of positional parameters: they are all providers of the
 * same shape, so `new MachineView(canvas, machine, 'high', a, b, c)` is a line where swapping
 * two arguments compiles cleanly and produces a machine with the lamps driven by the display.
 */
export interface MachineViewOptions {
  readonly lighting?: () => ComponentLightingState | undefined
  /**
   * Force a tier, as `?tier=low` or a settings value does. Absent means *decide* (M3-35).
   *
   * Naming a tier here suppresses the calibration, which is the point: measuring something
   * whose answer cannot be acted on costs five frames at the worst moment there is, the first
   * five of a machine load.
   */
  readonly tier?: RenderTier
  /**
   * A tier decision made elsewhere and shared.
   *
   * The audio voice pool is sized by tier too, and it is built on the first user gesture — which
   * can be long before anybody opens the 3-D view. Two independent decisions would be two
   * probes, and on a device near a threshold, two different answers.
   */
  readonly tierChoice?: TierChoice
  /** The dot-matrix raster the game's display driver last produced. */
  readonly dmdRaster?: () => DmdRaster | undefined
  /** How to read a lamp's state by id, from the rule engine. */
  readonly lampState?: () => LampStateReader | undefined
  /**
   * The physics events the simulation recorded since the last frame (M3-11).
   *
   * A provider rather than the feed itself, for the same reason the machine is: rebuilding
   * replaces the world, and a view holding the old feed would shake to a machine nobody is
   * playing.
   */
  readonly events?: () => PresentationEvents | undefined
  /**
   * The rule variables and running modes the machine's toys display (M3-07).
   *
   * Not the lamp reader. A toy is a state display of a *variable* — the star's appearance is the
   * Star Core charge — and routing it through a lamp would mean the rulesheet keeping a bulb in
   * step with a number, which is a second copy of the truth and one that can be wrong.
   */
  readonly toyState?: () => ToyStateReader | undefined
  /** Is the player asking for less flashing (NFR-026, M3-40)? Read per frame. */
  readonly flashReduced?: () => boolean
}

export class MachineView {
  readonly host: SceneHost
  readonly scene: MachineScene

  /**
   * Which tier this is rendering at and why (M3-35).
   *
   * Public because the settings panel drives it and because "why does this look like that" is a
   * question the HUD has to be able to answer — a tier chosen for a reason nobody can read is
   * indistinguishable from a renderer that is broken.
   */
  readonly tier: TierChoice

  /** The runtime governor (M3-36). Steps the tier down; cannot reach the simulation. */
  readonly governor: FrameGovernor

  /**
   * Camera shake, and the preference that decides whether there is any (M3-11).
   *
   * Public because the settings panel sets `motion.intensity`, and because a test that cannot
   * reach the shake can only prove the camera moved, not that it moved for a reason.
   */
  readonly shake = new CameraShake()
  readonly motion = new MotionPreference()

  /**
   * The Free camera's orbit (M3-10), driven by the mouse (M3-31).
   *
   * Created with a placeholder and moved onto the authored Free pose the first time that preset
   * is shown — the pose needs the machine's dimensions, and those come from the definition on
   * the frame rather than from the constructor.
   */
  readonly orbit = new OrbitDrag({ azimuth: 0, elevation: 0.5, distance: 1.5 })
  private orbitPlaced = false
  private readonly detachOrbit: () => void

  private preset: CameraPreset = 'player'
  private readonly machine: () => LoadedMachine
  private readonly events: () => PresentationEvents | undefined
  private readonly target = new Vector3()
  private readonly offset: ShakeOffset = createShakeOffset()
  /** How much of the event feed this view has already folded into the shake. */
  private eventCursor = 0
  /** Context recoveries already accounted for, so a rebuilt scene is not measured as slow. */
  private seenRecoveries = 0
  /** A one-shot line for the player, set when the governor steps. */
  private pendingNotice: string | undefined

  constructor(
    canvas: HTMLCanvasElement,
    machine: () => LoadedMachine,
    options: MachineViewOptions = {},
  ) {
    this.machine = machine
    this.events = options.events ?? (() => undefined)
    this.scene = new MachineScene(
      machine,
      options.dmdRaster ?? (() => undefined),
      options.lampState ?? (() => undefined),
      options.toyState ?? (() => undefined),
      // Read per frame, not captured: a player who turns reduced motion on mid-game expects the
      // star to settle immediately, and the same call is what the camera shake already reads.
      () => this.motion.scale,
      options.flashReduced ?? (() => false),
      options.lighting ?? (() => undefined),
    )
    this.tier =
      options.tierChoice ??
      new TierChoice(options.tier === undefined ? {} : { forced: options.tier })
    /*
      **A view that cannot be built gives back what its fields already took** (M11-02, L-02).

      `new SceneHost` is where WebGL fails — a locked-down browser, a blocklisted driver, a canvas
      whose context was lost a moment ago. `MachineRuntime` catches that and tells the shell, which
      is right: the simulation keeps running and the page says there is no picture.

      What was wrong is what the throw left behind. The field initialisers above have **already
      run** by the time the constructor body starts, so `motion` is holding a live
      `prefers-reduced-motion` listener on a half-built object that nothing can reach — `this.view`
      is never assigned, so `dispose()` has nothing to call. Measured: one leaked listener per
      failed construction, which under React's StrictMode is one per mount.

      So the failure path releases the fields that acquired something, then lets the error out
      unchanged. The caller's behaviour does not change; the wreckage does not persist.
    */
    try {
      this.host = new SceneHost(canvas, this.scene, { tier: this.tier.tier })

      if (this.tier.needsCalibration) this.calibrate()
      this.governor = new FrameGovernor(this.host.tier)

      this.detachOrbit = attachPointerOrbit(canvas, this.orbit)
      this.applyCamera(0)
    } catch (error) {
      // Only the fields that took something. `host` may not exist — that is usually what threw —
      // and `detachOrbit` is assigned after it, so there is nothing to detach when it does.
      this.motion.dispose()
      throw error
    }
  }

  get camera(): CameraPreset {
    return this.preset
  }

  /**
   * Measure this device and settle on a tier it can hold (M3-35, 08 §6 step 2).
   *
   * Measure, ask, step down, **measure again** — not measure once and divide. The three flat
   * tiers share a frame budget because they share a target of sixty frames a second; what
   * differs is what each spends to get there, and no arithmetic over the budget table can tell
   * a device what `medium` would have cost it. Only `medium` can.
   *
   * Runs in the constructor, which is the one moment the frames are free: the scene is built,
   * nothing is moving, and nobody has been shown anything yet. It costs five frames per tier
   * tried, and it tries a second tier only on a device that already failed the first — where
   * those frames were going to be slow whatever happened next. The answer is then stored, so a
   * returning player on the same hardware pays none of it.
   */
  private calibrate(): void {
    let tier = this.host.tier
    for (;;) {
      const frameMs = this.host.calibrate()
      if (fitsFrameBudget(tier, frameMs)) break
      const next = tierBelow(tier)
      if (next === undefined) break
      tier = next
      // Rebuilds the scene, so the next `calibrate()` pays for its own shader compilation in
      // its warm-up frames rather than charging it to this device's frame rate.
      this.host.setTier(tier)
    }
    this.tier.calibrated(tier)
  }

  /**
   * Step the shake intensity down, wrapping at the bottom (M3-11).
   *
   * The shell's settings panel will own a slider; this is the same value, stepped, so the
   * *off* position is reachable by hand. `prefers-reduced-motion` still overrides it in both
   * directions — the system preference is not a slider, and a player who set it does not want
   * the game's own control able to turn motion back on.
   */
  cycleMotion(): number {
    const steps = MOTION_STEPS
    const at = steps.findIndex((s) => Math.abs(s - this.motion.intensity) < 1e-9)
    this.motion.intensity = steps[(at + 1) % steps.length]!
    return this.motion.intensity
  }

  /**
   * Put the camera on a named preset (M3-32).
   *
   * The phone's portrait layout wants Follow, per 08 §5.1 — a machine shown at 72% of a phone
   * screen is too small to read the top of the playfield from, and Follow is the preset that
   * brings the action to the middle. Separate from `cycleCamera`, which is the harness stepping
   * through them by hand.
   */
  setCamera(preset: CameraPreset): CameraPreset {
    this.preset = preset
    // Only Free listens to the mouse. Left enabled under another preset, a drag would move
    // nothing visible and then be waiting, applied in full, the moment somebody chose Free.
    this.orbit.enabled = preset === 'free'
    return this.preset
  }

  cycleCamera(): CameraPreset {
    const i = CAMERA_PRESETS.indexOf(this.preset)
    this.preset = CAMERA_PRESETS[(i + 1) % CAMERA_PRESETS.length]!
    // Only Free listens to the mouse. Left enabled, a drag under the Player camera would move
    // nothing visible and then be waiting, applied in full, the moment somebody pressed C.
    this.orbit.enabled = this.preset === 'free'
    return this.preset
  }

  /** The machine was rebuilt: throw the scene away and build it from the new world. */
  rebuild(): void {
    this.host.rebuildScene()
    // The feed was cleared with the machine, so its cursor starts again; keeping the old one
    // would skip every event of the new game until it caught up with the dead one's count.
    this.eventCursor = 0
    this.shake.reset()
    // A rebuilt scene compiles its programs again. Those frames are not this device's speed.
    this.governor.settle()
  }

  resize(): void {
    this.host.resize()
  }

  /**
   * Force a tier, or hand the decision back with `undefined` (M3-35, 08 §6 step 4).
   *
   * The settings panel's control, and the harness's `G` key standing in for it. Returns the tier
   * now in force, which is not necessarily the one asked for: clearing an override returns to
   * whatever was last *measured* on this device.
   */
  setTierOverride(tier: RenderTier | undefined): RenderTier {
    const now = this.tier.setOverride(tier)
    this.host.setTier(now)
    this.governor.adopt(now)
    return now
  }

  /**
   * A notice the player should see, once (M3-36's "discreet notice").
   *
   * Taken rather than read, because the shell shows it as a transient line and a notice that
   * kept being returned would be a toast that never went away.
   */
  takeNotice(): string | undefined {
    const notice = this.pendingNotice
    this.pendingNotice = undefined
    return notice
  }

  /**
   * Draw one frame at the driver's alpha. Returns false while the GPU is away.
   *
   * `frameMs` is the wall-clock interval since the previous frame, which is the governor's only
   * input (M3-36). Optional because the interval belongs to whoever owns the animation-frame
   * loop, and a caller with no clock — a test, a screenshot pass — should not have to invent one
   * to draw a picture. Omitted, the governor simply never steps.
   */
  render(alpha: number, frameMs?: number): boolean {
    this.applyCamera(alpha)
    const drew = this.host.render(alpha)
    if (drew && frameMs !== undefined) this.govern(frameMs)
    return drew
  }

  /**
   * Feed the governor and act on what it decides.
   *
   * Only on frames that were actually drawn: while the context is gone the loop keeps running
   * and the intervals keep arriving, and charging a dead GPU with being slow would step the tier
   * down for a machine that was not being asked to render at all.
   */
  private govern(frameMs: number): void {
    const recoveries = this.host.contextLoss.recoveries
    if (recoveries !== this.seenRecoveries) {
      this.seenRecoveries = recoveries
      // Everything was rebuilt from nothing. Same reasoning as a tier change.
      this.governor.settle()
      return
    }

    const stepped = this.governor.sample(frameMs)
    if (stepped === undefined) return
    this.host.setTier(stepped)
    this.tier.stepped(stepped)
    this.pendingNotice = this.governor.lastNotice?.notice
  }

  dispose(): void {
    this.detachOrbit()
    this.motion.dispose()
    this.host.dispose()
  }

  private applyCamera(alpha: number): void {
    const loaded = this.machine()
    const dims = loaded.definition.dimensions
    const incline = (dims.inclineDegrees * Math.PI) / 180
    const frame: MachineFrame = {
      length: dims.cabinet?.length ?? 1.295,
      width: dims.cabinet?.width ?? 0.724,
      lockdownBarHeight: dims.lockdownBarHeight ?? 0.914,
      rise: dims.playfield.length * Math.sin(incline),
    }

    // Follow needs the ball in *world* space, and the ball is in playfield-local. Asking the
    // scene graph to convert is the only version of this that cannot drift: the transform it
    // uses is the one the ball is actually drawn with.
    let focus: readonly [number, number, number] | undefined
    if (this.preset === 'follow') {
      const ball = loaded.world.balls.find((b) => b.active && b.heldBy < 0)
      if (ball !== undefined) {
        this.target.set(ball.x, ball.y, ball.z)
        this.scene.playfield.localToWorld(this.target)
        focus = [this.target.x, this.target.y, this.target.z]
      }
    }

    const pose = cameraPose(this.preset, frame, focus)
    const cam = this.host.camera
    // Free is the player's own orbit and is never re-framed under them; Overhead is already
    // framed on the playfield by construction. The rest get the narrow-pane fit (M3-32).
    const framed =
      this.preset === 'free' || this.preset === 'overhead'
        ? pose.position
        : fitEye(pose.position, pose.target, frame, pose.fov, cam.aspect)
    const place = this.preset === 'free' ? this.freePosition(pose) : framed
    cam.position.set(place[0], place[1], place[2])
    cam.lookAt(pose.target[0], pose.target[1], pose.target[2])
    // Widened on a narrow pane, so the machine keeps its width instead of losing both side rails
    // (M3-32). `cam.aspect` is whatever `SceneHost.resize` last measured, so this follows a
    // rotation and a keyboard opening without being told about either.
    const fov = fovForAspect(pose.fov, cam.aspect)
    if (cam.fov !== fov) {
      cam.fov = fov
      cam.updateProjectionMatrix()
    }

    this.applyShake(loaded, alpha)
  }

  /**
   * M3-10. Where the Free camera actually is, once the player has moved it.
   *
   * The authored pose is the *starting* orbit and not a position the camera returns to: it is
   * read off the pose once, with `orbitFrom`, so the first drag continues from exactly what was
   * on screen instead of snapping to whatever the orbit was initialised with. After that the
   * orbit is the player's, and it survives cycling away to another preset and back — a view
   * somebody set up by hand and then lost by pressing C twice is worse than no orbit at all.
   */
  private freePosition(pose: { position: readonly [number, number, number]; target: readonly [number, number, number] }): readonly [number, number, number] {
    if (!this.orbitPlaced) {
      this.orbitPlaced = true
      this.orbit.enabled = true
      this.orbit.reset(orbitFrom(pose.position, pose.target))
    }
    const { azimuth, elevation, distance } = this.orbit.orbit
    return orbitPosition(pose.target, azimuth, elevation, distance)
  }

  /**
   * M3-11. Fold the frame's events into the shake and displace the posed camera.
   *
   * After `lookAt`, and in the camera's *local* axes: `translateX`/`translateY` move along the
   * view's own right and up, and `rotateZ` rolls about the view axis. Shaking in world axes
   * would be a different effect from every preset — under the Overhead camera "up" is down the
   * playfield, so a world-space shake would slide the machine lengthways instead of jolting it.
   */
  private applyShake(loaded: LoadedMachine, alpha: number): void {
    this.shake.scale = this.motion.scale
    const feed = this.events()
    if (feed !== undefined) {
      this.eventCursor = feed.read(this.eventCursor, (type, _device, _ball, step, value) => {
        this.shake.add(type, value, step)
      })
    }

    const offset = this.shake.sample(loaded.world.stepIndex, alpha, this.offset)
    if (offset.x === 0 && offset.y === 0 && offset.roll === 0) return

    const cam = this.host.camera
    cam.translateX(offset.x)
    cam.translateY(offset.y)
    cam.rotateZ(offset.roll)
  }
}
